import { z } from 'zod'
import {
	CheckboxFieldSchema,
	ChildCreateSchema,
	CustomZodIssueCode,
	EntityNameSchema,
} from '#app/utils/zod-helpers.js'
import { UserProfileAiCreateSchema } from './user-profile-ai._._schema'
import { UserProfileCreateSchema } from './user-profile._._schema'
import { UserRoleCreateSchema } from './user-role._._schema'
import {
	USER_ONBOARDING_STATUS,
	USER_STATUS,
	USER_TYPE,
} from './user._._definitions'

export const PasswordSchema = z
	.string({ required_error: 'Password is required' })
	.min(6, { message: 'Password is too short' })
	// NOTE: bcrypt has a limit of 72 bytes (which should be plenty long)
	// https://github.com/epicweb-dev/epic-stack/issues/918
	.refine((val) => new TextEncoder().encode(val).length <= 72, {
		message: 'Password is too long',
	})

// Regular expression to validate name characters
// allowed characters:
// - letters (uppercase and lowercase)
// - spaces
// - hyphens
// - apostrophes
// - and some special characters
const nameRegex = /^[a-zA-ZÀ-ÖØ-öø-ÿ' -]+$/
export const UserNameSchema = EntityNameSchema.regex(nameRegex, {
	message: 'Name contains invalid characters',
})

export const UserFirstNameSchema = z
	.string({ required_error: 'First Name is required' })
	.trim()
	.min(2, { message: 'First Name is too short' })
	.max(100, { message: 'First Name is too long' })
	.regex(nameRegex, {
		message: 'First Name contains invalid characters',
	})

export const UserLastNameSchema = z
	.string({ required_error: 'Last Name is required' })
	.trim()
	.min(1, { message: 'Last Name is too short' })
	.max(100, { message: 'Last Name is too long' })
	.regex(nameRegex, {
		message: 'Last Name contains invalid characters',
	})

export const EmailSchema = z
	.string({ required_error: 'Email is required' })
	.trim()
	.email({ message: 'Email is invalid' })
	.min(3, { message: 'Email is too short' })
	.max(100, { message: 'Email is too long' })
	// users can type the email in any case, but we store it in lowercase
	.transform((value) => value.toLowerCase())

export const PasswordAndConfirmPasswordSchema = z
	.object({ password: PasswordSchema, confirmPassword: PasswordSchema })
	.superRefine(({ confirmPassword, password }, ctx) => {
		if (confirmPassword !== password) {
			ctx.addIssue({
				path: ['confirmPassword'],
				code: CustomZodIssueCode,
				message: 'The passwords must match',
			})
		}
	})

const PasswordCreateSchema = z.object({
	create: z.object({
		hash: PasswordSchema,
	}),
})

export const generateRandomPassword = () => {
	return Math.random().toString(36).slice(-8)
}

const UserStatusSchema = z.nativeEnum(USER_STATUS)
const UserTypeSchema = z.nativeEnum(USER_TYPE)
const UserOnboardingStatusSchema = z.nativeEnum(USER_ONBOARDING_STATUS)

const BaseUserSchema = z.object({
	name: UserFirstNameSchema,
	lastName: UserLastNameSchema,
	email: EmailSchema,
	type: UserTypeSchema,
	status: UserStatusSchema,
	onboardingStatus: UserOnboardingStatusSchema,
	oneSignal: z.string().transform((val, ctx) => {
		if (!val) return undefined
		try {
			const parsed = JSON.parse(val)
			return z.object({
				id: z.string(),
				type: z.string(),
				createdAt: z.string(),
				device_model: z.string().nullable(),
				carrier: z.string().nullable(),
				timezone_id: z.string().nullable(),
			}).parse(parsed)
		} catch {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: 'Invalid JSON format for oneSignal',
			})
			return z.NEVER
		}
	}).optional(),
})

export const UserCreateSchema = BaseUserSchema.partial().extend({
	name: UserFirstNameSchema,
	lastName: UserLastNameSchema,
	email: EmailSchema,
	password: PasswordCreateSchema,
	roles: UserRoleCreateSchema.optional(),
	profile: ChildCreateSchema(UserProfileCreateSchema).optional(),
	profileAi: ChildCreateSchema(UserProfileAiCreateSchema).optional(),
	oneSignal: z.string().nullable().optional(),
})

export const UserAiCreateSchema = z.object({
	name: UserNameSchema,
})

export const UserEditorSchema = z.object({
	name: UserFirstNameSchema,
	lastName: UserLastNameSchema,
})

export const UserEditConversationStateEnabledSchema = z.object({
	id: z.string(),
	conversationStateEnabled: CheckboxFieldSchema,
})
export type UserEditConversationStateEnabledSchema = z.infer<
	typeof UserEditConversationStateEnabledSchema
>
export type UserEditConversationStateEnabledInput = z.input<
	typeof UserEditConversationStateEnabledSchema
>

export const UserEditPushNotificationsEnabledSchema = z.object({
	id: z.string(),
	pushNotificationsEnabled: CheckboxFieldSchema,
})
export type UserEditPushNotificationsEnabledSchema = z.infer<
	typeof UserEditPushNotificationsEnabledSchema
>
export const UserEditPushNotificationsEnabledInputSchema = z.object({
	id: z.string(),
	pushNotificationsEnabled: z.boolean(),
})
export type UserEditPushNotificationsEnabledInput = z.input<
	typeof UserEditPushNotificationsEnabledInputSchema
>

export const UserEditEmailNotificationsEnabledSchema = z.object({
	id: z.string(),
	emailNotificationsEnabled: CheckboxFieldSchema,
})
export type UserEditEmailNotificationsEnabledSchema = z.infer<
	typeof UserEditEmailNotificationsEnabledSchema
>
export const UserEditEmailNotificationsEnabledInputSchema = z.object({
	id: z.string(),
	emailNotificationsEnabled: z.boolean(),
})
export type UserEditEmailNotificationsEnabledInput = z.input<
	typeof UserEditEmailNotificationsEnabledInputSchema
>

export const UserEditSchema = BaseUserSchema.partial()

export const UserEditStatusSchema = z.object({
	id: z.string(),
	status: UserStatusSchema,
	redirectTo: z.string().optional(),
})
export type UserEditStatusSchema = z.infer<typeof UserEditStatusSchema>
export type UserEditStatusInput = z.input<typeof UserEditStatusSchema>

export const UserEditOnboardingStatusSchema = z.object({
	id: z.string(),
	onboardingStatus: UserOnboardingStatusSchema,
	redirectTo: z.string().optional(),
})
export type UserEditOnboardingStatusSchema = z.infer<
	typeof UserEditOnboardingStatusSchema
>
export type UserEditOnboardingStatusInput = z.input<
	typeof UserEditOnboardingStatusSchema
>

export const UserReportSchema = z.object({
	reportedUserId: z.string(),
    reason: z.string(),
})